import { useState, useRef } from 'react'
import { PDFDocument, rgb, StandardFonts, degrees } from 'pdf-lib'
import { Upload, Download, FileText, Type } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function PDFWatermark() {
  const [file, setFile] = useState(null)
  const [watermarkedPdf, setWatermarkedPdf] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [watermarkText, setWatermarkText] = useState('DRAFT')
  const [opacity, setOpacity] = useState(0.3)
  const [fontSize, setFontSize] = useState(50)
  const fileInputRef = useRef(null)

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile)
      setWatermarkedPdf(null)
    } else if (selectedFile) {
      alert('Please select a PDF file')
    }
  }

  const addWatermark = async () => {
    if (!file) {
      alert('Please select a PDF file')
      return
    }

    if (!watermarkText.trim()) {
      alert('Please enter watermark text')
      return
    }

    setProcessing(true)
    try {
      const arrayBuffer = await file.arrayBuffer()
      const pdfDoc = await PDFDocument.load(arrayBuffer)
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica)
      const pages = pdfDoc.getPages()

      pages.forEach(page => {
        const { width, height } = page.getSize()
        page.drawText(watermarkText, {
          x: width / 2,
          y: height / 2,
          size: fontSize,
          font: helveticaFont,
          color: rgb(0.5, 0.5, 0.5),
          opacity: opacity,
          rotate: degrees(-45),
        })
      })

      const pdfBytes = await pdfDoc.save()
      const blob = new Blob([pdfBytes], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      setWatermarkedPdf({ url, blob })
    } catch (error) {
      alert('Error adding watermark: ' + error.message)
    } finally {
      setProcessing(false)
    }
  }

  const downloadWatermarked = () => {
    if (!watermarkedPdf) return
    const link = document.createElement('a')
    link.href = watermarkedPdf.url
    link.download = `watermarked-${file.name}`
    link.click()
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Select PDF File:</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileSelect}
              className="hidden"
              id="pdf-upload"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              type="button"
            >
              <Upload className="w-5 h-5 mr-2" />
              {file ? 'Change PDF' : 'Upload PDF'}
            </Button>
            {file && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-medium text-gray-900">{file.name}</span>
                </div>
              </div>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Watermark Text:</label>
            <input
              type="text"
              value={watermarkText}
              onChange={(e) => setWatermarkText(e.target.value)}
              className="input w-full"
              placeholder="Enter watermark text"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 font-semibold mb-3">Opacity:</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.1"
                  value={opacity}
                  onChange={(e) => setOpacity(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="w-12 text-center text-sm font-medium">{opacity.toFixed(1)}</span>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-3">Font Size:</label>
              <input
                type="number"
                min="10"
                max="200"
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                className="input w-full"
              />
            </div>
          </div>

          <Button
            onClick={addWatermark}
            variant="primary"
            className="w-full"
            disabled={!file || processing || !watermarkText.trim()}
          >
            <Type className="w-5 h-5 mr-2" />
            {processing ? 'Adding Watermark...' : 'Add Watermark'}
          </Button>
        </Card.Body>
      </Card>

      {watermarkedPdf && (
        <Card>
          <Card.Body>
            <div className="text-center space-y-4">
              <p className="text-green-600 font-semibold">Watermark added successfully!</p>
              <Button onClick={downloadWatermarked} variant="primary" size="lg" className="w-full">
                <Download className="w-5 h-5 mr-2" />
                Download Watermarked PDF
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {!file && (
        <Card>
          <Card.Body className="text-center py-12">
            <Type className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Upload a PDF file to add a watermark</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

